import React, { useEffect, useRef, useState } from 'react';
import { api } from '../../config/api';
import { util } from '../../config/util';
import { Cookies } from '../../constants/Cookies';
import GlobalEnvironments from '../../modules/globalEnvironment/GlobalEnvironment';
import ExpressCheckoutModal from './components/ExpressCheckoutModal';

// API 응답 인터페이스 - 새로운 4가지 결제 유형
interface SubscriptionProduct {
  id: number;
  name: string;
  isPopular: boolean;
  // 새로운 4가지 결제 유형
  regular_monthly_price: number; // 1개월 일반결제 (기준가, 가장 비쌈)
  regular_yearly_price: number; // 1년 일반결제 (약간 할인)
  subscription_monthly_price: number; // 월간 구독 결제 (매월 자동결제)
  subscription_yearly_price: number; // 연간 구독 결제 (1년치 자동결제)
  // 마케팅용 월 환산 금액들
  marketing_monthly_regular: number; // 1년 일반결제시 월 환산 (마케팅용)
  marketing_monthly_subscription: number; // 연간 구독시 월 환산 (마케팅용)
  // 기존 필드들 (하위 호환성 유지)
  origin_price?: number; // 기존 월간 가격
  yearly_price?: number; // 기존 연간 가격
  features: string[];
  description: string;
  category?: string;
}

// 섹션 7-8: Pricing Section과 FAQ Section을 원본 HTML과 완전히 동일하게 구현
const Section7and8: React.FC = () => {
  const pricingSectionRef = useRef<HTMLElement>(null);
  const [isMonthly, setIsMonthly] = useState(true);
  const [selectedPlans, setSelectedPlans] = useState<string[]>([]);
  const [isSubscription, setIsSubscription] = useState(true); // 구독형을 기본값으로 설정
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [pricingPlans, setPricingPlans] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);
  
  // Express Checkout 모달 상태
  const [isExpressCheckoutOpen, setIsExpressCheckoutOpen] = useState(false);
  
  // 사용자 로그인 상태 체크
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  
  // 결제 정보 상태 (DOM 조작 대신 React 상태 사용)
  const [paymentInfo, setPaymentInfo] = useState({
    planName: '플랜을 선택해주세요',
    planPrice: '선택한 플랜의 요금이 표시됩니다',
    isEnabled: false
  });
  
  // 결제 바 표시 상태
  const [isPaymentBarVisible, setIsPaymentBarVisible] = useState(false);
  
  // 로그인 상태 확인 함수 (기존 로그인 페이지의 로직 활용)
  const checkLoginStatus = () => {
    const key = util.getCookie(Cookies.authKey);
    return !!key;
  };
  
  // 소셜 로그인 함수 (기존 로그인 페이지의 로직 활용)
  const handleSocialLogin = async (provider: 'kakao' | 'naver' | 'google') => {
    try {
      if (provider === 'kakao') {
        // Kakao SDK 초기화 확인
        if (!window.Kakao?.isInitialized()) {
          window.Kakao?.init("4e6c55bc6919807933f9f9c2620e7e9d");
        }
        window.Kakao?.Auth.authorize({
          redirectUri: `${window.location.origin}/oauth2/kakao`,
        });
      } else if (provider === 'naver') {
        const clientId = "5KdLca8FOBTswLiRvb6v";
        const redirectUri = `${window.location.origin}/oauth2/naver`;
        const naverLoginUrl = `https://nid.naver.com/oauth2.0/authorize?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}`;
        window.location.href = naverLoginUrl;
      } else if (provider === 'google') {
        const clientId = "419125396470-2rt62duahcpf2odq0nggbm8tskui2rjo.apps.googleusercontent.com";
        const redirectUri = `${window.location.origin}/oauth2/google`;
        const googleLoginUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=email+profile`;
        window.location.href = googleLoginUrl;
      }
    } catch (error) {
      console.error('소셜 로그인 오류:', error);
      throw error;
    }
  };
  
  // 전화번호 인증 함수 (기존 회원가입 페이지의 로직 활용)
  const handlePhoneAuth = async (phoneNumber: string) => {
    try {
      // 실제 구현시 아임포트 본인인증 로직 필요
      // 여기서는 임시로 성공 처리
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      return {
        success: true,
        userData: {
          phone: phoneNumber,
          name: '사용자',
          provider: 'phone'
        }
      };
    } catch (error) {
      console.error('전화번호 인증 오류:', error);
      throw error;
    }
  };

  // 기본 가격 플랜 데이터 (API 실패시 fallback)
  const defaultPricingPlans = [
    {
      id: 'basic',
      name: '베이직',
      description: '개인 건축사/소형 건설사를 위한 기본 플랜',
      monthlyPrice: 29000,
      yearlyPrice: 23200,
      originalMonthly: 36250,
      originalYearly: 29000,
      features: [
        '월 100회 일위대가 검증',
        '기본 검증 리포트',
        '이메일 지원',
        '표준품셈 데이터베이스'
      ]
    },
    {
      id: 'pro',
      name: '프로',
      description: '중소 건설회사를 위한 완벽한 솔루션',
      monthlyPrice: 79000,
      yearlyPrice: 63200,
      originalMonthly: 98750,
      originalYearly: 79000,
      isPopular: true,
      features: [
        '무제한 일위대가 검증',
        '표준품셈 연동',
        '팀 협업 (10명)',
        '우선 지원',
        'AI 검증 리포트',
        '배치 검증 기능'
      ]
    },
    {
      id: 'enterprise',
      name: '엔터프라이즈',
      description: '대형 건설사를 위한 맞춤형 솔루션',
      monthlyPrice: 149000,
      yearlyPrice: 119200,
      originalMonthly: 186250,
      originalYearly: 149000,
      features: [
        '모든 프로 기능',
        '무제한 사용자',
        '전담 매니저',
        '맞춤 개발',
        '온사이트 교육',
        '무제한 데이터 저장'
      ]
    }
  ];

  // 새로운 4가지 결제 유형에 따른 가격 계산
  const calculatePrice = (product: SubscriptionProduct) => {
    if (isSubscription) {
      // 구독 결제 (자동결제)
      if (!isMonthly) {
        // 연간 구독 결제 (1년치 자동결제)
        return product.subscription_yearly_price;
      }
      // 월간 구독 결제 (매월 자동결제)
      return product.subscription_monthly_price;
    } else {
      // 일반 결제
      if (!isMonthly) {
        // 1년 일반결제 (약간 할인)
        return product.regular_yearly_price;
      }
      // 1개월 일반결제 (기준가, 가장 비쌈)
      return product.regular_monthly_price;
    }
  };

  // API에서 구독 상품 로드 (SubscriptionPaymentContainer 스타일로 개선)
  const loadSubscriptionProducts = async () => {
    try {
      setIsLoading(true);
      setApiError(null);
      
      console.log("🔍 [Section7and8] 상품 API 호출 시작...");
      const productsRes = await api.getSubscriptionProductsAsync();
      console.log("✅ [Section7and8] 상품 API 응답:", productsRes);

      if (productsRes?.status === 200) {
        // API 응답 상태 확인
        if (productsRes.data?.code !== 1) {
          console.error("❌ API 응답 코드 에러:", productsRes.data?.code, productsRes.data?.message);
          setApiError(productsRes.data?.message || "상품 정보를 불러오는데 실패했습니다.");
          setPricingPlans(defaultPricingPlans);
          return;
        }

        // API 응답 데이터 검증 - 새로운 응답 구조 대응
        const rawProducts = productsRes.data.data?.products || productsRes.data.data || productsRes.data.products;
        console.log("📦 [DB 원본] 추출한 상품 데이터:", rawProducts);
        
        // DB 원본 데이터의 각 상품별 가격 정보 상세 로깅
        if (Array.isArray(rawProducts)) {
          rawProducts.forEach((product: any, index: number) => {
            console.log(`💰 [상품 ${index + 1}] DB 가격 데이터:`, {
              id: product.id,
              name: product.name,
              origin_price: product.origin_price,
              subscription_monthly_price: product.subscription_monthly_price,
              subscription_yearly_price: product.subscription_yearly_price,
              yearly_price: product.yearly_price,
              monthly_price: product.monthly_price,
              original_price: product.original_price,
              discount_percent: product.discount_percent
            });
          });
        }

        if (!Array.isArray(rawProducts)) {
          console.error("❌ Products data is not an array:", rawProducts);
          setApiError("상품 데이터 형식이 올바르지 않습니다.");
          setPricingPlans(defaultPricingPlans);
          return;
        }

        // API 응답 데이터를 인터페이스에 맞게 변환 (null safety 추가)
        const validProducts = rawProducts
          .filter((product: any) => {
            if (!product || typeof product !== "object") {
              console.warn("⚠️ Invalid product object:", product);
              return false;
            }
            if (!product.id) {
              console.warn("⚠️ Product missing ID:", product);
              return false;
            }
            return true;
          })
          .slice(0, 3); // 상위 3개 상품만 선택

        if (validProducts.length === 0) {
          console.warn("⚠️ No valid products found after filtering");
          setPricingPlans(defaultPricingPlans);
          setApiError("유효한 상품이 없습니다. 기본 플랜을 표시합니다.");
          return;
        }

        // API 데이터를 새로운 4가지 결제 유형에 맞게 변환
        const convertedPlans = validProducts.map((product: any, index: number) => {
          // 새로운 4가지 결제 유형 데이터 매핑
          const productData = {
            id: product.id,
            name: product.name || `상품 ${index + 1}`,
            isPopular: product.is_popular || product.category === "인기" || index === 1,
            // 새로운 4가지 결제 유형
            regular_monthly_price: product.regular_monthly_price || product.origin_price || 0,
            regular_yearly_price: product.regular_yearly_price || product.yearly_price || 0,
            subscription_monthly_price: product.subscription_monthly_price || 0,
            subscription_yearly_price: product.subscription_yearly_price || 0,
            // 마케팅용 월 환산 금액들
            marketing_monthly_regular: product.marketing_monthly_regular || Math.floor((product.regular_yearly_price || product.yearly_price || 0) / 12),
            marketing_monthly_subscription: product.marketing_monthly_subscription || Math.floor((product.subscription_yearly_price || 0) / 12),
            // 기존 필드들 (하위 호환성)
            origin_price: product.origin_price || 0,
            yearly_price: product.yearly_price || 0,
            features: Array.isArray(product.features) && product.features.length > 0 
              ? product.features 
              : [
                  '월 100회 일위대가 검증',
                  '기본 검증 리포트',
                  '이메일 지원',
                  '표준품셈 데이터베이스'
                ],
            description: product.description || "상품 설명이 없습니다.",
            category: product.category || ""
          };
          
          console.log(`🔄 [상품 ${index + 1}] 변환된 가격:`, {
            name: productData.name,
            regular_monthly_price: productData.regular_monthly_price,
            regular_yearly_price: productData.regular_yearly_price,
            subscription_monthly_price: productData.subscription_monthly_price,
            subscription_yearly_price: productData.subscription_yearly_price,
            marketing_monthly_regular: productData.marketing_monthly_regular,
            marketing_monthly_subscription: productData.marketing_monthly_subscription
          });
          
          return {
            id: productData.id.toString(),
            name: productData.name,
            description: productData.description,
            // 이 필드들은 하위 호환성을 위해 유지하지만 실제로는 rawProduct를 통해 계산됨
            monthlyPrice: productData.subscription_monthly_price || productData.regular_monthly_price,
            yearlyPrice: productData.subscription_yearly_price || productData.regular_yearly_price,
            isPopular: productData.isPopular,
            features: productData.features,
            rawProduct: productData // 실제 계산에 사용되는 데이터
          };
        });
        
        console.log("✨ [Section7and8] 변환된 플랜 데이터:", convertedPlans);
        setPricingPlans(convertedPlans);
        
      } else {
        console.error("❌ API 응답 상태 에러:", productsRes?.status);
        setApiError(`서버 응답 오류 (${productsRes?.status})`);
        setPricingPlans(defaultPricingPlans);
      }
    } catch (error: any) {
      console.error('❌ 구독 상품 로드 실패:', error);
      console.error('🔍 에러 상세:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        stack: error.stack
      });
      
      // 에러 유형별 메시지 설정 (더 상세하게)
      let errorMessage = '상품 정보를 불러오는데 실패했습니다.';
      
      if (!error.response) {
        // 네트워크 에러 또는 CORS 에러
        if (error.message?.includes('Network')) {
          errorMessage = '네트워크 연결을 확인해주세요.';
        } else if (error.message?.includes('CORS')) {
          errorMessage = 'API 접근 권한 문제가 발생했습니다.';
        }
      } else {
        // HTTP 에러
        const status = error.response.status;
        switch (true) {
          case status === 401:
            errorMessage = '인증이 필요합니다. 로그인 후 다시 시도해주세요.';
            break;
          case status === 403:
            errorMessage = '접근 권한이 없습니다.';
            break;
          case status === 404:
            errorMessage = 'API 엔드포인트를 찾을 수 없습니다.';
            break;
          case status === 429:
            errorMessage = '너무 많은 요청이 발생했습니다. 잠시 후 다시 시도해주세요.';
            break;
          case status >= 500:
            errorMessage = '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
            break;
          default:
            console.error(`API 오류 코드: ${status}`);
            errorMessage = '일시적인 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
        }
      }
      
      setApiError(errorMessage);
      // API 실패시 기본 데이터 사용
      setPricingPlans(defaultPricingPlans);
    } finally {
      setIsLoading(false);
      console.log("🏁 [Section7and8] 상품 로딩 완료");
    }
  };

  // 컴포넌트 마운트시 API 호출 및 로그인 상태 확인
  useEffect(() => {
    loadSubscriptionProducts();
    setIsLoggedIn(checkLoginStatus());
    
    // 개발용: 콘솔에서 API 직접 호출 함수 노출
    if (process.env.NODE_ENV === 'development') {
      (window as any).debugAPI = {
        loadProducts: loadSubscriptionProducts,
        checkPricingData: () => console.log('현재 pricingPlans:', pricingPlans)
      };
    }
  }, []);

  // 로그인 상태 변경 메시지 리스너
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      
      if (event.data.type === 'LOGIN_STATUS_CHANGED') {
        console.log('Section7and8 - 로그인 상태 변경:', event.data);
        setIsLoggedIn(event.data.isLoggedIn);
      }
    };

    window.addEventListener('message', handleMessage);
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // FAQ 데이터 (원본 HTML과 동일)
  const faqData = [
    {
      question: '콘엑스는 어떤 서비스인가요?',
      answer: '콘엑스는 건설업계의 일위대가 산출근거를 표준품셈과 비교 검증하는 AI 기반 자동화 시스템입니다. 일위대가의 정확성과 신뢰성을 향상시킵니다.'
    },
    {
      question: '무료로 사용할 수 있나요?',
      answer: '7일 무료 체험을 제공하며, 체험 기간 동안 모든 기능을 제한 없이 사용하실 수 있습니다. 체험 후에는 베이직, 프로, 엔터프라이즈 플랜 중 선택하실 수 있습니다.'
    },
    {
      question: '어떤 파일 형식을 지원하나요?',
      answer: '일위대가 데이터 (Excel .xlsx), 표준품셈 PDF 파일, JSON 형식을 지원합니다. 대량 일위대가 배치 처리도 가능하며, Claude API를 통한 지능형 검증을 제공합니다.'
    },
    {
      question: '데이터 보안은 어떻게 보장되나요?',
      answer: '모든 일위대가 데이터는 암호화되어 보관되며, 검증 완료 후 자동 삭제됩니다. 기업 내부 정보 보호를 위해 HTTPS 암호화 통신과 액세스 로그 관리를 제공합니다.'
    },
    {
      question: '결제는 어떻게 이루어지나요?',
      answer: '신용카드, 법인카드, 계좌이체를 지원합니다. 프로젝트별 사용량 과금 또는 월 정액 구독이 가능합니다.'
    }
  ];

  // 연락처 정보 (원본 HTML과 동일)
  const contactInfo = [
    {
      icon: '📧',
      title: '이메일 문의',
      value: 'support@conexs.kr'
    },
    {
      icon: '💬',
      title: '실시간 채팅',
      value: '평일 09:00 - 18:00'
    },
    {
      icon: '📞',
      title: '전화 상담',
      value: '02-1234-5678'
    }
  ];

  // 스크롤 애니메이션 초기화
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.scroll-animate');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // 결제 바 스크롤 감지
  useEffect(() => {
    const pricingSection = document.getElementById('pricing');
    
    if (!pricingSection) return;
    
    // 스크롤 이벤트로 가격 섹션 감지
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        setIsPaymentBarVisible(entry.isIntersecting);
      });
    }, { threshold: 0.1 });

    observer.observe(pricingSection);
    
    return () => observer.disconnect();
  }, []);

  // 월간/연간 토글이나 구독 설정이 변경될 때 결제 정보 업데이트
  useEffect(() => {
    updatePaymentInfo();
  }, [selectedPlans, isMonthly, isSubscription, pricingPlans]);

  const toggleBilling = () => {
    setIsMonthly(!isMonthly);
    // 토글 슬라이더 애니메이션
    const slider = document.getElementById('toggleSlider');
    if (slider) {
      slider.style.transform = isMonthly ? 'translateX(100%)' : 'translateX(0)';
    }
  };

  const selectPlan = (planId: string) => {
    if (selectedPlans.includes(planId)) {
      // 이미 선택된 플랜이면 선택 해제
      setSelectedPlans(selectedPlans.filter(id => id !== planId));
    } else {
      // 새로 선택된 플랜 추가
      setSelectedPlans([...selectedPlans, planId]);
    }
  };

  const updatePaymentInfo = () => {
    if (selectedPlans.length === 0) {
      // 선택된 플랜이 없을 때
      setPaymentInfo({
        planName: '플랜을 선택해주세요',
        planPrice: '선택한 플랜의 요금이 표시됩니다',
        isEnabled: false
      });
    } else {
      // 선택된 플랜이 있을 때
      const selectedPlanNames = selectedPlans.map(planId => {
        const plan = pricingPlans.find(p => p.id === planId);
        return plan ? plan.name : '';
      }).filter(name => name);
      
      const totalPrice = selectedPlans.reduce((total, planId) => {
        const plan = pricingPlans.find(p => p.id === planId);
        return total + (plan ? getCurrentPrice(plan) : 0);
      }, 0);
      
      const period = isMonthly ? '월' : '년';
      
      setPaymentInfo({
        planName: selectedPlanNames.length > 1 ? 
          `${selectedPlanNames.join(', ')} 플랜` : 
          `${selectedPlanNames[0]} 플랜`,
        planPrice: `${totalPrice.toLocaleString()}원/${period}`,
        isEnabled: true
      });
    }
  };

  const toggleSubscription = () => {
    setIsSubscription(!isSubscription);
    
    // 체크박스 애니메이션
    const checkbox = document.querySelector('.custom-checkbox');
    if (checkbox) {
      if (!isSubscription) {
        checkbox.classList.add('checked');
      } else {
        checkbox.classList.remove('checked');
      }
    }
  };

  const toggleFaq = (index: number) => {
    if (expandedFaq === index) {
      setExpandedFaq(null);
    } else {
      setExpandedFaq(index);
    }
  };

  const getCurrentPrice = (plan: typeof pricingPlans[0]) => {
    // 새로운 4가지 결제 유형에 따른 금액 반환
    const rawProduct = plan.rawProduct;
    if (!rawProduct) return plan.monthlyPrice || 0;
    
    return calculatePrice(rawProduct as SubscriptionProduct);
  };

  const getMonthlyEquivalent = (plan: typeof pricingPlans[0]) => {
    // 월 결제 환산 금액 반환 (연간 결제시만 사용)
    const rawProduct = plan.rawProduct;
    if (!rawProduct || isMonthly) return null;
    
    return isSubscription 
      ? rawProduct.marketing_monthly_subscription 
      : rawProduct.marketing_monthly_regular;
  };

  // 새로운 플로우: Express Checkout 관련 함수들
  const handlePaymentClick = () => {
    console.log('🚀 결제 버튼 클릭됨!', {
      selectedPlans,
      isLoggedIn,
      paymentInfo
    });
    
    if (selectedPlans.length === 0) {
      alert('먼저 플랜을 선택해주세요.');
      return;
    }

    // 새로운 플로우: 로그인 상태에 따른 분기
    if (isLoggedIn) {
      // 로그인된 상태 → 바로 결제 페이지로 이동 (기존 방식)
      console.log('✅ 로그인된 사용자 - 이용권 페이지로 이동');
      
      // 선택된 플랜 정보 저장 (이용권 페이지에서 활용)
      const selectedPlanInfo = {
        plans: getSelectedPlansForCheckout(),
        totalAmount: getTotalAmount(),
        isMonthly: isMonthly,
        isSubscription: isSubscription,
        timestamp: Date.now()
      };
      localStorage.setItem('selected_plans_from_landing', JSON.stringify(selectedPlanInfo));
      
      // 이용권 페이지로 즉시 이동
      window.location.href = '/subscription';
    } else {
      // 비로그인 상태 → Express Checkout 모달로 새로운 플로우 시작
      console.log('✅ 비로그인 사용자 - Express Checkout 모달 열기');
      setIsExpressCheckoutOpen(true);
    }
  };

  const proceedToPayment = () => {
    // 기존 결제 프로세스 (로그인 사용자용)
    console.log('기존 결제 프로세스로 이동');
    // 실제 구현에서는 결제 페이지로 이동하는 로직 필요
  };

  const handleExpressCheckoutSuccess = (user: any) => {
    console.log('🎉 [Section7and8] Express Checkout 성공:', user);
    setIsLoggedIn(true);
    setIsExpressCheckoutOpen(false);
    
    // 성공 메시지 표시 (짧게)
    const userName = user.name || user.email || '고객';
    
    // 로그인 상태 전파 (다른 컴포넌트들에게 알림)
    window.postMessage({ 
      type: 'LOGIN_STATUS_CHANGED', 
      isLoggedIn: true,
      user: user 
    }, window.location.origin);
    
    // 쿠키에 인증 정보 저장 (실제 토큰이 있다면)
    if (user.authKey || user.authToken) {
      const authKey = user.authKey || user.authToken;
      util.setCookie({
        cookieName: Cookies.authKey,
        cookieValue: authKey,
        cookieExpire: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000), // 1년
        cookieDomain: GlobalEnvironments.getDomainUrl(),
        cookiePath: "/",
      });
      console.log('🍪 인증 토큰이 쿠키에 저장되었습니다.');
    }
    
    // 새로운 플로우: 항상 이용권 페이지로 이동 (로그인 완료 + 카드정보 저장 후)
    console.log(`✅ ${userName}님, 로그인 및 카드정보 저장이 완료되었습니다! 이용권 페이지로 이동합니다.`);
    
    // 카드 정보 저장 (localStorage에 저장하여 이용권 페이지에서 활용)
    if (user.cardInfo) {
      localStorage.setItem('express_card_info', JSON.stringify(user.cardInfo));
      console.log('💳 카드 정보가 저장되었습니다. 이용권 페이지에서 바로 결제하실 수 있습니다.');
    }
    
    // 사용자 정보도 저장 (이용권 페이지에서 활용)
    if (user.name || user.email || user.phone) {
      const userInfo = {
        name: user.name,
        email: user.email,
        phone: user.phone,
        provider: user.provider,
        providerId: user.providerId,
        isLoggedIn: true
      };
      localStorage.setItem('express_user_info', JSON.stringify(userInfo));
      console.log('👤 사용자 정보가 저장되었습니다.');
    }
    
    // 선택된 플랜 정보 저장 (이용권 페이지에서 활용)
    const selectedPlanInfo = {
      plans: getSelectedPlansForCheckout(),
      totalAmount: getTotalAmount(),
      isMonthly: isMonthly,
      isSubscription: isSubscription,
      timestamp: Date.now(),
      source: 'express_checkout' // 출처 표시
    };
    localStorage.setItem('express_selected_plans', JSON.stringify(selectedPlanInfo));
    console.log('📋 선택된 플랜 정보가 저장되었습니다.');
    
    // 이용권 페이지로 이동 (1.5초 후)
    setTimeout(() => {
      console.log('🚀 [Section7and8] 이용권 페이지로 이동합니다...');
      window.location.href = '/subscription';
    }, 1500);
  };

  const handlePaymentComplete = (billingKey: string, subscriptionId: number) => {
    console.log('결제 완료:', { billingKey, subscriptionId });
    // 결제 완료 후 추가 처리 로직 (예: 분석 이벤트 전송, 대시보드 업데이트 등)
  };

  const handleExpressCheckoutClose = () => {
    setIsExpressCheckoutOpen(false);
  };

  // 선택된 플랜들을 Express Checkout에 맞는 형식으로 변환
  const getSelectedPlansForCheckout = () => {
    return selectedPlans.map(planId => {
      const plan = pricingPlans.find(p => p.id === planId);
      if (!plan) return null;
      
      return {
        id: plan.id,
        name: plan.name,
        monthlyPrice: plan.monthlyPrice,
        yearlyPrice: plan.yearlyPrice
      };
    }).filter(plan => plan !== null);
  };

  // 총 결제 금액 계산
  const getTotalAmount = () => {
    return selectedPlans.reduce((total, planId) => {
      const plan = pricingPlans.find(p => p.id === planId);
      return total + (plan ? getCurrentPrice(plan) : 0);
    }, 0);
  };

  return (
    <>
      {/* 7. 가격 섹션 */}
      <section className="section pricing-section" id="pricing" ref={pricingSectionRef}>
        <div className="pricing-container">
          <div className="pricing-header">
            <div className="pricing-step-label">STEP 01</div>
            <h2 className="pricing-title">인기 추천 플랜 선택</h2>
            <p className="pricing-subtitle">구독형 결제로 자동 결제 혜택</p>
            
            <div className="billing-toggle">
              <div className="toggle-wrapper">
                <div className="toggle-slider" id="toggleSlider"></div>
                <button 
                  className={`toggle-option ${isMonthly ? 'active' : ''}`} 
                  id="monthlyBtn"
                  onClick={toggleBilling}
                >
                  월간
                </button>
                <button 
                  className={`toggle-option ${!isMonthly ? 'active' : ''}`} 
                  id="yearlyBtn"
                  onClick={toggleBilling}
                >
                  연간
                </button>
              </div>
            </div>
          </div>

          {/* API 로딩 상태 */}
          {isLoading && (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div style={{ fontSize: '18px', color: '#64748b' }}>상품 정보를 불러오는 중...</div>
            </div>
          )}
          
          {/* API 에러 상태 */}
          {apiError && (
            <div style={{ textAlign: 'center', padding: '20px', background: '#f0f9ff', borderRadius: '12px', margin: '20px 0', border: '1px solid rgba(37, 99, 235, 0.1)' }}>
              <div style={{ color: '#1e40af', fontSize: '16px', marginBottom: '8px', fontWeight: '600' }}>{apiError}</div>
              <div style={{ color: '#64748b', fontSize: '14px' }}>아래는 체험용 기본 플랜입니다.</div>
            </div>
          )}

          <div className="pricing-grid">
            {pricingPlans.map((plan, index) => (
              <div 
                key={plan.id}
                className={`pricing-card ${selectedPlans.includes(plan.id) ? 'selected' : ''}`}
                data-plan={plan.id}
                data-monthly={plan.monthlyPrice}
                data-yearly={plan.yearlyPrice}
                onClick={() => selectPlan(plan.id)}
              >
                
                <div className="plan-name">{plan.name}</div>
                <div className="plan-desc">{plan.description}</div>
                
                <div className="price-section">
                  {/* 실제 결제 금액 */}
                  <div className="price-main">
                    <span id={`${plan.id}-price`}>{getCurrentPrice(plan).toLocaleString()}</span>원
                  </div>
                  
                  {/* 월 결제 환산 표시 (연간 결제시만) */}
                  <div className="price-period" id={`${plan.id}-period`}>
                    {!isMonthly && getMonthlyEquivalent(plan) ? (
                      <span>월 결제 환산 {getMonthlyEquivalent(plan)?.toLocaleString()}원</span>
                    ) : (
                      <span>{isMonthly ? '월 결제' : '연간 결제'}</span>
                    )}
                  </div>
                </div>
                
                <div className="features-list">
                  {plan.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="feature-item">
                      <div className="feature-icon"></div>
                      <div className="feature-text">{feature}</div>
                    </div>
                  ))}
                </div>
                
                <div className="select-indicator">
                  <span>선택하기</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 하단 고정 결제 바 */}
        <div 
          className="payment-bottom" 
          id="paymentBottom"
          style={{
            transform: isPaymentBarVisible ? 'translateY(0)' : 'translateY(100%)'
          }}
        >
          <div className="payment-content">
            <div className="pricing-payment-info">
              <div className="payment-icon">🛒</div>
              <div className="payment-details">
                <span>{paymentInfo.planName}</span>
                <span className="payment-separator">•</span>
                <span>{paymentInfo.planPrice}</span>
              </div>
            </div>
            <button 
              className="payment-button" 
              disabled={!paymentInfo.isEnabled}
              onClick={handlePaymentClick}
              style={{
                opacity: paymentInfo.isEnabled ? '1' : '0.5'
              }}
            >
              🚀 3분만에 시작하기
            </button>
          </div>
        </div>
      </section>

      {/* 8. FAQ 섹션 */}
      <section className="section faq-section scroll-animate" id="faq">
        <div className="faq-container">
          <div className="faq-header">
            <h2>자주 묻는 질문</h2>
            <p>콘엑스 이용에 대해 궁금한 점들을 확인해보세요</p>
          </div>

          <div className="faq-list">
            {faqData.map((faq, index) => (
              <div 
                key={index} 
                className={`faq-item ${expandedFaq === index ? 'active' : ''}`}
              >
                <button 
                  className="faq-question"
                  onClick={() => toggleFaq(index)}
                >
                  <span>{faq.question}</span>
                  <div className="faq-icon">▼</div>
                </button>
                <div className="faq-answer">
                  <div className="faq-answer-content">
                    {faq.answer}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* 문의 정보 섹션 */}
          <div className="contact-section">
            <h3>더 궁금한 점이 있으신가요?</h3>
            <p>언제든지 편리한 방법으로 문의해주세요. 빠르고 정확한 답변을 드리겠습니다.</p>
            
            <div className="contact-info">
              {contactInfo.map((contact, index) => (
                <div key={index} className="contact-item">
                  <div className="contact-icon">{contact.icon}</div>
                  <div className="contact-title">{contact.title}</div>
                  <div className="contact-value">{contact.value}</div>
                </div>
              ))}
            </div>
            
            <a href="#contact" className="contact-button">💬 바로 문의하기</a>
          </div>
        </div>
      </section>

      {/* Express Checkout 모달 */}
      <ExpressCheckoutModal
        isOpen={isExpressCheckoutOpen}
        onClose={handleExpressCheckoutClose}
        selectedPlans={getSelectedPlansForCheckout()}
        totalAmount={getTotalAmount()}
        isMonthly={isMonthly}
        isSubscription={isSubscription} // 사용자가 선택한 구독/일반 결제 방식
        isLoggedIn={isLoggedIn}
        onSuccess={handleExpressCheckoutSuccess}
        onSocialLogin={handleSocialLogin}
        onPhoneAuth={handlePhoneAuth}
        onPaymentComplete={handlePaymentComplete}
      />
    </>
  );
};

export default Section7and8;